import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from '@react-keycloak/web';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Avatar,
  Typography,
  Button,
  Badge,
  Spin,
  Empty
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  SunOutlined,
  ScheduleOutlined,
  BarChartOutlined,
  SettingOutlined,
  RightOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  CoffeeOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { getSummaryEmployee } from '../redux/slieces/summaryEmplyeeSlice';
import { computeEmployeeStats, getCurrentISOWeek, getDefaultCalendarWeek } from '../core/utils/summaryStats';
import styles from './Dashboard.module.css';

const { Title, Text } = Typography;

const Dashboard = () => {
  const { t } = useTranslation();
  const { keycloak, initialized } = useKeycloak();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(new Date());

  const { loading = false, summaryEmployee = [] } = useSelector(
    (state) => state.summaryEmployee || {}
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (initialized && keycloak?.authenticated) {
      dispatch(getSummaryEmployee());
    }
  }, [initialized, keycloak?.authenticated, dispatch]);

  const userName = keycloak?.tokenParsed?.preferred_username || keycloak?.tokenParsed?.name || t('dashboard.user');
  const userEmail = keycloak?.tokenParsed?.email || '';

  // Admin-only sections (Employees, Week Template settings) must be hidden for
  // regular users — matches the Sidebar gating and the route-level guards.
  const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes('mediadmin');

  // Get the logged-in user's own summary. For admins, getSummaryEmployee()
  // returns the whole organisation, so we must match by email rather than
  // assume index 0 (which would show another employee's overtime/vacation).
  const normalizedEmail = userEmail.trim().toLowerCase();
  const currentUserData =
    summaryEmployee.find(
      (e) => e.email && e.email.trim().toLowerCase() === normalizedEmail
    ) || null;

  // Calculate statistics from real data
  const calculateStats = () => {
    if (!currentUserData) {
      return {
        totalOvertime: 0,
        remainingVacation: 0,
        vacationCarryover: 0,
        thisWeekHours: 0,
        thisWeekTarget: 0,
        completedWeeks: 0
      };
    }

    const currentYear = new Date().getFullYear();
    const currentWeek = getCurrentISOWeek();

    // Use the SAME figures the Summary page shows: current year, up to the last
    // completed week, with paid overtime deducted. This keeps the Dashboard and
    // Summary in agreement (previously the Dashboard summed all weeks and
    // ignored paid overtime, so its numbers disagreed with the Summary).
    const s = computeEmployeeStats(currentUserData, {
      years: [currentYear],
      upToWeek: getDefaultCalendarWeek()
    });

    // The "This Week" card reflects the current (in-progress) week, so it looks
    // up the current-week entry directly rather than the completed-week cutoff.
    const thisWeekEntry = (currentUserData.summaryCalendarEmployees || []).find(
      (e) => e.year === currentYear && e.weekCalendarNumber === currentWeek
    );

    return {
      totalOvertime: Math.round(s.overtimeHours * 10) / 10,
      remainingVacation: s.remainingVacation,
      vacationCarryover: s.vacationCarryover,
      thisWeekHours: thisWeekEntry
        ? thisWeekEntry.actualWorkTimeHours + thisWeekEntry.actualWorkTimeMinutes / 60
        : 0,
      thisWeekTarget: thisWeekEntry
        ? thisWeekEntry.shouldWorkTimeHours + thisWeekEntry.shouldWorkTimeMinutes / 60
        : 40,
      completedWeeks: s.completedWeeks
    };
  };

  const stats = calculateStats();

  const quickActions = [
    {
      key: 'weekplan',
      title: t('dashboard.weekPlan') || 'Week Plan',
      description: t('dashboard.weekPlanDesc') || 'View and edit your weekly schedule',
      icon: <ScheduleOutlined />,
      color: '#1890ff',
      path: '/week-plan'
    },
    {
      key: 'summary',
      title: t('dashboard.summary') || 'Summary',
      description: t('dashboard.summaryDesc') || 'View your work time summary',
      icon: <BarChartOutlined />,
      color: '#52c41a',
      path: '/summary'
    },
    {
      key: 'employees',
      title: t('dashboard.employees') || 'Employees',
      description: t('dashboard.employeesDesc') || 'Manage employee templates',
      icon: <TeamOutlined />,
      color: '#722ed1',
      path: '/employees',
      adminOnly: true
    },
    {
      key: 'settings',
      title: t('dashboard.settings') || 'Settings',
      description: t('dashboard.settingsDesc') || 'Configure week templates',
      icon: <SettingOutlined />,
      color: '#fa8c16',
      path: '/settings/week-template',
      adminOnly: true
    }
  ].filter((action) => !action.adminOnly || isAdmin);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('dashboard.goodMorning') || 'Good Morning';
    if (hour < 18) return t('dashboard.goodAfternoon') || 'Good Afternoon';
    return t('dashboard.goodEvening') || 'Good Evening';
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!initialized) {
    return (
      <div className={styles.loadingContainer}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        <Text style={{ marginTop: 16 }}>{t('loadingAuth') || 'Loading...'}</Text>
      </div>
    );
  }

  return (
    <div className={styles.dashboard}>
      {/* Header Section */}
      <div className={styles.dashboardHeader}>
        <div className={styles.welcomeSection}>
          <div className={styles.welcomeContent}>
            <Title level={2} className={styles.welcomeTitle}>
              {getGreeting()}, {userName}!
            </Title>
            <div className={styles.dateTimeDisplay}>
              <div className={styles.dateDisplay}>
                <CalendarOutlined className={styles.dateIcon} />
                <Text className={styles.dateText}>{formatDate(currentTime)}</Text>
              </div>
              <div className={styles.timeDisplay}>
                <ClockCircleOutlined className={styles.timeIcon} />
                <Text className={styles.currentTime}>{formatTime(currentTime)}</Text>
              </div>
            </div>
          </div>
          <div className={styles.userCard}>
            <Avatar size={56} icon={<UserOutlined />} className={styles.userAvatar} />
            <div className={styles.userInfo}>
              <Text strong className={styles.userNameText}>{userName}</Text>
              <Text type="secondary" className={styles.userEmailText}>{userEmail}</Text>
              <Badge status="success" text={t('dashboard.online') || 'Online'} className={styles.statusBadge} />
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[24, 24]} className={styles.statsRow}>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard} loading={loading}>
            <div className={styles.statContent}>
              <div className={styles.statIconWrapper} style={{ background: 'linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)' }}>
                <FieldTimeOutlined className={styles.statIconInner} />
              </div>
              <Statistic
                title={t('dashboard.overtimeBalance') || 'Overtime Balance'}
                value={stats.totalOvertime}
                precision={1}
                suffix={t('hours') || 'hrs'}
                valueStyle={{
                  color: stats.totalOvertime >= 0 ? '#3f8600' : '#cf1322',
                  fontWeight: 700
                }}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard} loading={loading}>
            <div className={styles.statContent}>
              <div className={styles.statIconWrapper} style={{ background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)' }}>
                <SunOutlined className={styles.statIconInner} />
              </div>
              <Statistic
                title={t('dashboard.remainingVacation') || 'Remaining Vacation'}
                value={stats.remainingVacation}
                suffix={t('days_name') || 'days'}
                valueStyle={{ color: '#1890ff', fontWeight: 700 }}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard} loading={loading}>
            <div className={styles.statContent}>
              <div className={styles.statIconWrapper} style={{ background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)' }}>
                <ClockCircleOutlined className={styles.statIconInner} />
              </div>
              <Statistic
                title={t('dashboard.thisWeekHours') || 'This Week'}
                value={Math.round(stats.thisWeekHours * 10) / 10}
                suffix={`/ ${stats.thisWeekTarget} ${t('hours') || 'hrs'}`}
                valueStyle={{ color: '#722ed1', fontWeight: 700 }}
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className={styles.statCard} loading={loading}>
            <div className={styles.statContent}>
              <div className={styles.statIconWrapper} style={{ background: 'linear-gradient(135deg, #fa8c16 0%, #ffa940 100%)' }}>
                <CheckCircleOutlined className={styles.statIconInner} />
              </div>
              <Statistic
                title={t('dashboard.completedWeeks') || 'Completed Weeks'}
                value={stats.completedWeeks}
                suffix={`/ 52`}
                valueStyle={{ color: '#fa8c16', fontWeight: 700 }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* This Week Progress */}
      <Row gutter={[24, 24]} className={styles.progressRow}>
        <Col xs={24}>
          <Card
            className={styles.progressCard}
            title={
              <div className={styles.cardTitleWrapper}>
                <ScheduleOutlined className={styles.cardTitleIcon} />
                <span>{t('dashboard.weekProgress') || 'This Week\'s Progress'}</span>
              </div>
            }
          >
            {loading ? (
              <div className={styles.loadingPlaceholder}>
                <Spin />
              </div>
            ) : (
              <div className={styles.weekProgressContent}>
                <div className={styles.progressStats}>
                  <div className={styles.progressStatItem}>
                    <Text type="secondary">{t('dashboard.worked') || 'Worked'}</Text>
                    <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
                      {Math.round(stats.thisWeekHours * 10) / 10}h
                    </Title>
                  </div>
                  <div className={styles.progressStatItem}>
                    <Text type="secondary">{t('dashboard.target') || 'Target'}</Text>
                    <Title level={3} style={{ margin: 0, color: '#595959' }}>
                      {stats.thisWeekTarget}h
                    </Title>
                  </div>
                  <div className={styles.progressStatItem}>
                    <Text type="secondary">{t('dashboard.difference') || 'Difference'}</Text>
                    <Title
                      level={3}
                      style={{
                        margin: 0,
                        color: stats.thisWeekHours - stats.thisWeekTarget >= 0 ? '#52c41a' : '#fa8c16'
                      }}
                    >
                      {stats.thisWeekHours - stats.thisWeekTarget >= 0 ? '+' : ''}
                      {Math.round((stats.thisWeekHours - stats.thisWeekTarget) * 10) / 10}h
                    </Title>
                  </div>
                </div>
                <Progress
                  percent={Math.min(Math.round((stats.thisWeekHours / stats.thisWeekTarget) * 100), 100)}
                  strokeColor={{
                    '0%': '#1890ff',
                    '100%': '#52c41a'
                  }}
                  trailColor="#f0f0f0"
                  strokeWidth={12}
                  className={styles.weekProgressBar}
                />
                <div className={styles.progressLabels}>
                  <Text type="secondary">0h</Text>
                  <Text type="secondary">{Math.round(stats.thisWeekTarget / 2)}h</Text>
                  <Text type="secondary">{stats.thisWeekTarget}h</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Row gutter={[24, 24]} className={styles.actionsRow}>
        <Col xs={24}>
          <Card
            className={styles.actionsCard}
            title={
              <div className={styles.cardTitleWrapper}>
                <CoffeeOutlined className={styles.cardTitleIcon} />
                <span>{t('dashboard.quickActions') || 'Quick Actions'}</span>
              </div>
            }
          >
            <div className={styles.actionGrid}>
              {quickActions.map((action) => (
                <div
                  key={action.key}
                  className={styles.actionItem}
                  onClick={() => navigate(action.path)}
                >
                  <div
                    className={styles.actionIconWrapper}
                    style={{ background: `linear-gradient(135deg, ${action.color} 0%, ${action.color}dd 100%)` }}
                  >
                    {action.icon}
                  </div>
                  <div className={styles.actionContent}>
                    <Text strong className={styles.actionTitle}>{action.title}</Text>
                    <Text type="secondary" className={styles.actionDescription}>
                      {action.description}
                    </Text>
                  </div>
                  <RightOutlined className={styles.actionArrow} />
                </div>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Current Week Info */}
      <Row gutter={[24, 24]} className={styles.infoRow}>
        <Col xs={24}>
          <Card className={styles.infoCard}>
            <div className={styles.infoContent}>
              <CalendarOutlined className={styles.infoIcon} />
              <div className={styles.infoText}>
                <Text strong>
                  {t('dashboard.currentWeek') || 'Current Week'}: {getCurrentISOWeek(currentTime)}
                </Text>
                <Text type="secondary">
                  {t('dashboard.year') || 'Year'} {currentTime.getFullYear()}
                </Text>
              </div>
              <Button
                type="primary"
                icon={<ScheduleOutlined />}
                onClick={() => navigate('/week-plan')}
              >
                {t('dashboard.openWeekPlan') || 'Open Week Plan'}
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
